<template>
  <!-- 顶部导航栏（Amber Logic 设计） -->
  <header class="header" :class="{ 'header--scrolled': isScrolled }">
    <div class="container">
      <div class="header__content">
        <!-- Logo 区域 -->
        <div class="header__logo" @click="goHome">
          <div class="header__logo-icon">
            <img src="@/static/logo.jpg" alt="米晴科技" class="logo-image" />
          </div>
          <span class="header__logo-text">米晴科技</span>
        </div>

        <!-- 桌面端导航菜单 -->
        <nav class="header__nav desktop-only">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="header__nav-item"
          >
            {{ item.name }}
          </router-link>
        </nav>

        <!-- 右侧 CTA 按钮 -->
        <div class="header__actions desktop-only">
          <router-link to="/apply" class="header__cta">
            成为合作伙伴
          </router-link>
        </div>

        <!-- 移动端菜单按钮 -->
        <div
          class="header__menu-btn mobile-only"
          :class="{ 'is-active': isMobileMenuOpen }"
          @click="toggleMobileMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <!-- 移动端导航菜单 -->
    <transition name="mobile-menu">
      <div v-if="isMobileMenuOpen" class="header__mobile-menu mobile-only">
        <nav class="header__mobile-nav">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="header__mobile-nav-item"
            @click.native="closeMobileMenu"
          >
            {{ item.name }}
          </router-link>
          <router-link
            to="/apply"
            class="header__mobile-cta"
            @click.native="closeMobileMenu"
          >
            成为合作伙伴
          </router-link>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: 'Header',

  data() {
    return {
      isScrolled: false,
      isMobileMenuOpen: false,
      navItems: [
        { name: '首页', path: '/' },
        { name: '产品服务', path: '/products' },
        { name: '场景方案', path: '/solutions' },
        { name: '服务案例', path: '/cases' },
        { name: '关于我们', path: '/about' }
      ]
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20
    },

    goHome() {
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    },

    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },

    closeMobileMenu() {
      this.isMobileMenuOpen = false
    }
  }
}
</script>

<style lang="scss" scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: $header-height;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba($border-color, 0.6);
  transition: all $transition-normal;
  z-index: $z-index-sticky;

  @include mobile {
    height: 64px;
  }

  &--scrolled {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    background-color: rgba(255, 255, 255, 0.95);
  }

  &__content {
    @include flex-between;
    height: $header-height;

    @include mobile {
      height: 64px;
    }
  }

  // Logo
  &__logo {
    @include flex-center;
    gap: $spacing-sm;
    cursor: pointer;
    transition: opacity $transition-normal;

    &:hover {
      opacity: 0.85;
    }
  }

  &__logo-icon {
    width: 40px;
    height: 40px;
    border-radius: $border-radius-md;
    overflow: hidden;
    @include flex-center;

    @include mobile {
      width: 32px;
      height: 32px;
    }

    .logo-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__logo-text {
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    color: $primary-color;
    letter-spacing: -0.02em;

    @include mobile {
      font-size: $font-size-base;
    }
  }

  // 桌面端导航
  &__nav {
    @include flex-center;
    gap: $spacing-xl + 8;

    @include mobile {
      display: none !important;
    }
  }

  &__nav-item {
    position: relative;
    padding: $spacing-sm 0;
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: $text-secondary;
    transition: color $transition-normal;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 2px;
      background-color: $primary-color;
      transition: width $transition-normal;
      border-radius: 2px;
    }

    &:hover {
      color: $primary-color;
    }

    &.router-link-exact-active {
      color: $primary-color;
      font-weight: $font-weight-semibold;

      &::after {
        width: 100%;
      }
    }
  }

  // 右侧操作
  &__actions {
    @include flex-center;
  }

  // CTA 按钮
  &__cta {
    @include button-primary;
    @include button-large;
    color: $on-primary !important;

    &:hover {
      color: $on-primary !important;
    }
  }

  // 移动端菜单按钮
  &__menu-btn {
    display: none;
    width: 26px;
    height: 20px;
    flex-direction: column;
    justify-content: space-between;
    cursor: pointer;

    @include mobile {
      display: flex !important;
    }

    span {
      display: block;
      width: 100%;
      height: 2px;
      background-color: $text-primary;
      border-radius: 2px;
      transition: all $transition-normal;
      transform-origin: center;
    }

    &.is-active {
      span:nth-child(1) {
        transform: translateY(9px) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: translateY(-9px) rotate(-45deg);
      }
    }
  }

  // 移动端导航菜单
  &__mobile-menu {
    display: none;
    position: fixed;
    top: 64px;
    left: 0;
    width: 100%;
    height: calc(100vh - 64px);
    background-color: $bg-white;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    z-index: $z-index-fixed;
    overflow-y: auto;

    @include mobile {
      display: block !important;
    }
  }

  &__mobile-nav {
    @include flex-column;
    padding: $spacing-lg $spacing-md;
    gap: $spacing-xs;
  }

  &__mobile-nav-item {
    padding: $spacing-md $spacing-lg;
    font-size: $font-size-base;
    font-weight: $font-weight-medium;
    color: $text-primary;
    border-radius: $border-radius-md;
    transition: all $transition-normal;

    &:hover {
      color: $primary-color;
      background-color: rgba($primary-color, 0.06);
    }

    &.router-link-exact-active {
      color: $primary-color;
      background-color: rgba($primary-color, 0.08);
      font-weight: $font-weight-semibold;
    }
  }

  &__mobile-cta {
    @include button-primary;
    @include button-large;
    margin-top: $spacing-md;
    color: $on-primary !important;

    &:hover {
      color: $on-primary !important;
    }
  }
}

// 移动端菜单过渡
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all $transition-normal;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
